import { useEffect, useRef, useState } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { CheckCircle2, Clock, XCircle, Upload, Camera, ShieldCheck, ArrowRight, RotateCcw, AlertCircle } from 'lucide-react';

// Curated list — Binance-parity Tier-1 countries. Free-text option at the top for edge cases.
const COUNTRIES = [
  'United States', 'United Kingdom', 'India', 'Canada', 'Australia', 'Germany',
  'France', 'Italy', 'Spain', 'Netherlands', 'Poland', 'Portugal', 'Ireland',
  'Sweden', 'Norway', 'Finland', 'Denmark', 'Switzerland', 'Austria', 'Belgium',
  'Singapore', 'Japan', 'South Korea', 'Hong Kong', 'Taiwan', 'Malaysia',
  'Thailand', 'Vietnam', 'Philippines', 'Indonesia', 'United Arab Emirates',
  'Saudi Arabia', 'Turkey', 'South Africa', 'Nigeria', 'Kenya', 'Egypt',
  'Brazil', 'Mexico', 'Argentina', 'Chile', 'Colombia',
  'Other (specify below)'
];

// Compress an image file to at most maxDim px on the long side + JPEG q=0.85.
// Cuts a raw 6 MB phone photo to ~200-400 KB — keeps base64 under request-size
// limits AND still readable by admin reviewers.
async function compressImageToDataUrl(file, maxDim = 1600, quality = 0.85) {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onerror = reject;
    fr.onload = () => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, w, h);
        ctx.drawImage(img, 0, 0, w, h);
        try {
          resolve(canvas.toDataURL('image/jpeg', quality));
        } catch (_e) {
          // Fallback for CORS-tainted canvas (shouldn't happen for user-picked files)
          resolve(fr.result);
        }
      };
      img.onerror = reject;
      img.src = fr.result;
    };
    fr.readAsDataURL(file);
  });
}

// Rough KB size of a data URL string.
function dataUrlSizeKb(s) {
  if (!s) return 0;
  const commaIdx = s.indexOf(',');
  const b64 = commaIdx > 0 ? s.slice(commaIdx + 1) : s;
  return Math.round((b64.length * 0.75) / 1024);
}

function FileDrop({ label, hint, value, onChange, testid }) {
  const inputRef = useRef();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const handleFile = async (file) => {
    setErr('');
    if (!file) return;
    if (!/^image\//i.test(file.type)) { setErr('Please upload an image file (JPG/PNG)'); return; }
    if (file.size > 15 * 1024 * 1024) { setErr('File too large (max 15 MB before compression)'); return; }
    setBusy(true);
    try {
      const dataUrl = await compressImageToDataUrl(file);
      onChange(dataUrl);
    } catch (_e) {
      setErr('Could not read image — try a different file');
    } finally { setBusy(false); }
  };

  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-[#848E9C] mb-1">{label}</div>
      {hint && <div className="text-[11px] text-[#5E6673] mb-2">{hint}</div>}
      <div onClick={() => !busy && inputRef.current?.click()}
        className={`border border-dashed ${err ? 'border-[#F6465D]/60' : 'border-[#2B3139] hover:border-[#FCD535]/50'} bg-[#0B0E11] rounded-sm p-4 cursor-pointer transition-colors`}
        data-testid={testid}>
        {busy ? (
          <div className="text-center py-8 text-[#FCD535] text-sm">Compressing image…</div>
        ) : value ? (
          <div className="relative">
            <img src={value} alt="preview" className="max-h-48 mx-auto object-contain" />
            <div className="mt-2 flex items-center justify-between text-[10px] font-mono text-[#5E6673]">
              <span>{dataUrlSizeKb(value)} KB · compressed</span>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onChange(''); setErr(''); }}
                className="text-[#F6465D] hover:text-[#F6465D]/80 inline-flex items-center gap-1"
                data-testid={`${testid}-remove`}
              >
                <RotateCcw className="w-3 h-3" /> Replace
              </button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center py-6 text-[#848E9C]">
            <Upload className="w-6 h-6 mb-2" />
            <div className="text-sm">Click to upload image</div>
            <div className="text-[10px] mt-1">JPG or PNG · max 15 MB</div>
          </div>
        )}
        <input ref={inputRef} type="file" accept="image/*"
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])} />
      </div>
      {err && (
        <div className="text-[11px] text-[#F6465D] mt-1 flex items-center gap-1" data-testid={`${testid}-error`}>
          <AlertCircle className="w-3 h-3" /> {err}
        </div>
      )}
    </div>
  );
}

const LOCAL_STORAGE_KEY = 'cx_kyc_draft_v1';

export default function Kyc() {
  const { user, refresh } = useAuth();
  const [status, setStatus] = useState(null);
  const [step, setStep] = useState(1);
  const [fullName, setFullName] = useState('');
  const [country, setCountry] = useState('');
  const [countryOther, setCountryOther] = useState('');
  const [docType, setDocType] = useState('passport');
  const [docNumber, setDocNumber] = useState('');
  const [idFront, setIdFront] = useState('');
  const [idBack, setIdBack] = useState('');
  const [selfie, setSelfie] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // Tier 2 state
  const [t2Addr1, setT2Addr1] = useState('');
  const [t2Addr2, setT2Addr2] = useState('');
  const [t2City, setT2City] = useState('');
  const [t2Postal, setT2Postal] = useState('');
  const [t2Country, setT2Country] = useState('');
  const [t2ProofType, setT2ProofType] = useState('utility_bill');
  const [t2Proof, setT2Proof] = useState('');
  const [t2Submitting, setT2Submitting] = useState(false);

  // Restore draft (personal info + doc number only — never persist images to
  // localStorage, they'd blow the 5 MB quota).
  useEffect(() => {
    try {
      const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (raw) {
        const d = JSON.parse(raw);
        if (d.fullName) setFullName(d.fullName);
        if (d.country) setCountry(d.country);
        if (d.countryOther) setCountryOther(d.countryOther);
        if (d.docType) setDocType(d.docType);
        if (d.docNumber) setDocNumber(d.docNumber);
        if (d.step && d.step <= 3) setStep(d.step);
      }
    } catch (_e) {}
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify({
        fullName, country, countryOther, docType, docNumber, step,
      }));
    } catch (_e) {}
  }, [fullName, country, countryOther, docType, docNumber, step]);

  const load = async () => {
    try {
      const s = await api.get('/kyc/status');
      setStatus(s.data);
    } catch (_e) { setStatus({ status: 'not_started' }); }
  };
  useEffect(() => { load(); }, []);

  const effectiveCountry = country === 'Other (specify below)' ? countryOther.trim() : country;

  const submit = async () => {
    if (!fullName || !effectiveCountry || !docNumber || !idFront || !selfie) {
      toast.error('Please fill every required field'); return;
    }
    // Passport doesn't need a back side; everything else does.
    if (docType !== 'passport' && !idBack) {
      toast.error('Back side of document is required for this document type'); return;
    }
    setSubmitting(true);
    try {
      await api.post('/kyc/submit', {
        full_name: fullName,
        country: effectiveCountry,
        document_type: docType,
        document_number: docNumber,
        id_front_base64: idFront,
        id_back_base64: idBack || null,
        selfie_base64: selfie,
      });
      toast.success('KYC submitted — pending admin review');
      localStorage.removeItem(LOCAL_STORAGE_KEY);
      // Reset in-memory images so they don't linger
      setIdFront(''); setIdBack(''); setSelfie('');
      await refresh();
      await load();
      setStep(1);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Submission failed');
    } finally { setSubmitting(false); }
  };

  const submitTier2 = async () => {
    if (!t2Addr1 || !t2City || !t2Postal || !t2Country || !t2Proof) {
      toast.error('Fill every field and upload proof-of-address'); return;
    }
    setT2Submitting(true);
    try {
      await api.post('/kyc/tier2/submit', {
        address_line1: t2Addr1, address_line2: t2Addr2 || null,
        city: t2City, postal_code: t2Postal, country: t2Country,
        proof_type: t2ProofType, proof_document_base64: t2Proof,
      });
      toast.success('Tier 2 submitted — pending admin review');
      setT2Proof('');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Submission failed');
    } finally { setT2Submitting(false); }
  };

  const kycStatus = status?.status || user?.kyc_status || 'not_started';
  const tier = status?.tier ?? (kycStatus === 'verified' ? 1 : 0);
  // Detect an existing pending Tier 2 submission (status='pending' AND tier===2)
  const pendingTier2 = status?.submission?.tier === 2 && status?.submission?.status === 'pending';
  const statusConfig = {
    not_started: { icon: Upload, color: '#848E9C', label: 'Not Started',
                    desc: 'Complete verification to lift the non-KYC withdrawal cap and unlock all features.' },
    pending:     { icon: Clock, color: '#FCD535', label: 'Under Review',
                    desc: 'Your documents are being reviewed by our team. Typically takes a few minutes to a few hours.' },
    verified:    { icon: CheckCircle2, color: '#0ECB81', label: 'Verified',
                    desc: 'Your identity is verified. Non-KYC withdrawal cap removed — you can now withdraw any amount within regular limits.' },
    rejected:    { icon: XCircle, color: '#F6465D', label: 'Rejected',
                    desc: 'Verification could not be completed. Please review the reason below and resubmit.' },
  }[kycStatus];
  const StatusIcon = statusConfig.icon;

  const canContinueStep1 = fullName.trim() && effectiveCountry;
  const canContinueStep2 = docNumber.trim() && idFront && (docType === 'passport' || idBack);
  const canSubmit = canContinueStep1 && canContinueStep2 && selfie && !submitting;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
      <h1 className="font-display text-white text-4xl font-bold tracking-tighter flex items-center gap-3">
        <ShieldCheck className="w-8 h-8 text-[#FCD535]" /> Identity Verification
      </h1>
      <p className="text-[#848E9C] text-sm mt-1 mb-8">
        Verification is optional up to $50,000 lifetime withdrawals. Beyond that, KYC is required.
      </p>

      {/* Status card */}
      <div className="border border-[#2B3139] bg-[#181A20] p-6 mb-8 flex items-start gap-4" data-testid="kyc-status-card">
        <div className="w-12 h-12 rounded-full grid place-items-center flex-shrink-0" style={{ background: `${statusConfig.color}22` }}>
          <StatusIcon className="w-6 h-6" style={{ color: statusConfig.color }} />
        </div>
        <div className="flex-1">
          <div className="text-xs uppercase tracking-wider text-[#848E9C]">Status</div>
          <div className="text-xl font-semibold" style={{ color: statusConfig.color }}>{statusConfig.label}</div>
          <div className="text-[#B7BDC6] text-sm mt-1">{statusConfig.desc}</div>
          {kycStatus === 'rejected' && status?.submission?.rejection_reason && (
            <div className="mt-3 border border-[#F6465D]/40 bg-[#F6465D]/10 p-3 rounded-sm text-xs text-[#F6465D]" data-testid="kyc-rejection-reason">
              <span className="font-semibold uppercase">Reason:</span> {status.submission.rejection_reason}
            </div>
          )}
          {status?.submission?.submitted_at && (
            <div className="text-[10px] font-mono text-[#5E6673] mt-2">
              Last submitted {new Date(status.submission.submitted_at).toLocaleString()}
            </div>
          )}
        </div>
      </div>

      {kycStatus === 'verified' || kycStatus === 'pending' ? null : (
        <>
          {/* Steps indicator */}
          <div className="flex items-center gap-2 mb-6" data-testid="kyc-step-indicator">
            {[
              { n: 1, label: 'Personal Info' },
              { n: 2, label: 'Document' },
              { n: 3, label: 'Selfie & Submit' },
            ].map(({ n, label }) => (
              <div key={n} className="flex-1">
                <div className={`h-1 rounded-full transition-colors ${step >= n ? 'bg-[#FCD535]' : 'bg-[#2B3139]'}`} />
                <div className={`text-[10px] uppercase font-mono mt-1 ${step === n ? 'text-[#FCD535]' : step > n ? 'text-white' : 'text-[#5E6673]'}`}>
                  {n}. {label}
                </div>
              </div>
            ))}
          </div>

          <div className="border border-[#2B3139] bg-[#181A20] p-6" data-testid="kyc-form">
            {step === 1 && (
              <div className="space-y-4">
                <div className="text-white font-semibold text-lg">Personal Information</div>
                <div>
                  <Label className="text-[#B7BDC6] text-xs uppercase tracking-wider">Full Legal Name</Label>
                  <Input value={fullName} onChange={e => setFullName(e.target.value)}
                    placeholder="As shown on your ID document"
                    className="bg-[#0B0E11] border-[#2B3139] rounded-sm h-11 text-white mt-1.5 focus-visible:ring-[#FCD535]/40 focus-visible:border-[#FCD535]"
                    data-testid="kyc-fullname" />
                </div>
                <div>
                  <Label className="text-[#B7BDC6] text-xs uppercase tracking-wider">Country of Residence</Label>
                  <Select value={country} onValueChange={setCountry}>
                    <SelectTrigger className="bg-[#0B0E11] border-[#2B3139] rounded-sm text-white mt-1.5" data-testid="kyc-country-select">
                      <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#181A20] border-[#2B3139] text-white max-h-72">
                      {COUNTRIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {country === 'Other (specify below)' && (
                    <Input value={countryOther} onChange={e => setCountryOther(e.target.value)}
                      placeholder="Type your country"
                      className="bg-[#0B0E11] border-[#2B3139] rounded-sm h-11 text-white mt-2 focus-visible:ring-[#FCD535]/40 focus-visible:border-[#FCD535]"
                      data-testid="kyc-country-other" />
                  )}
                </div>
                <Button onClick={() => setStep(2)} disabled={!canContinueStep1}
                  className="bg-[#FCD535] hover:bg-[#F0B90B] text-[#0B0E11] font-semibold rounded-sm w-full h-11 disabled:opacity-40"
                  data-testid="kyc-next-1">
                  Continue <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-4">
                <div className="text-white font-semibold text-lg">Document</div>
                <div>
                  <Label className="text-[#B7BDC6] text-xs uppercase tracking-wider">Document Type</Label>
                  <Select value={docType} onValueChange={setDocType}>
                    <SelectTrigger className="bg-[#0B0E11] border-[#2B3139] rounded-sm text-white mt-1.5" data-testid="kyc-doctype"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#181A20] border-[#2B3139] text-white">
                      <SelectItem value="passport">Passport</SelectItem>
                      <SelectItem value="driver_license">Driver&apos;s License</SelectItem>
                      <SelectItem value="national_id">National ID</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[#B7BDC6] text-xs uppercase tracking-wider">Document Number</Label>
                  <Input value={docNumber} onChange={e => setDocNumber(e.target.value)}
                    placeholder="e.g. A12345678"
                    className="bg-[#0B0E11] border-[#2B3139] rounded-sm h-11 text-white mt-1.5 font-mono focus-visible:ring-[#FCD535]/40 focus-visible:border-[#FCD535]"
                    data-testid="kyc-docnumber" />
                </div>
                <FileDrop
                  label="Document — Front"
                  hint="Photo of the ID page. All 4 corners visible, no glare."
                  value={idFront} onChange={setIdFront}
                  testid="kyc-front-file" />
                {docType !== 'passport' && (
                  <FileDrop
                    label="Document — Back"
                    hint="Back side of ID card / driver's license."
                    value={idBack} onChange={setIdBack}
                    testid="kyc-back-file" />
                )}
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => setStep(1)}
                    className="border-[#2B3139] bg-[#0B0E11] text-white hover:bg-[#2B3139] rounded-sm flex-1">Back</Button>
                  <Button onClick={() => setStep(3)} disabled={!canContinueStep2}
                    className="bg-[#FCD535] hover:bg-[#F0B90B] text-[#0B0E11] font-semibold rounded-sm flex-1 disabled:opacity-40"
                    data-testid="kyc-next-2">
                    Continue <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-4">
                <div className="text-white font-semibold text-lg">Selfie & Submit</div>
                <div className="border border-[#FCD535]/30 bg-[#FCD535]/5 p-3 rounded-sm text-xs text-[#FCD535] flex items-start gap-2">
                  <Camera className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold uppercase">How to take a good selfie</div>
                    <ul className="text-[#B7BDC6] mt-1 space-y-0.5 list-disc list-inside">
                      <li>Hold your document next to your face — both clearly visible</li>
                      <li>Good lighting, no filters, no sunglasses or hats</li>
                      <li>Look straight at the camera, neutral expression</li>
                    </ul>
                  </div>
                </div>
                <FileDrop
                  label="Selfie with your document"
                  hint="Face + ID must both be readable in the same shot."
                  value={selfie} onChange={setSelfie}
                  testid="kyc-selfie-file" />

                {/* Compact review of what will be submitted */}
                <div className="border border-[#2B3139] bg-[#0B0E11] p-3 rounded-sm space-y-1 text-xs" data-testid="kyc-review-summary">
                  <div className="text-[10px] uppercase font-mono text-[#848E9C] mb-1">Review</div>
                  <div className="flex justify-between"><span className="text-[#848E9C]">Name</span><span className="text-white font-mono">{fullName || '—'}</span></div>
                  <div className="flex justify-between"><span className="text-[#848E9C]">Country</span><span className="text-white font-mono">{effectiveCountry || '—'}</span></div>
                  <div className="flex justify-between"><span className="text-[#848E9C]">Document</span><span className="text-white font-mono">{docType.replace('_', ' ')} · {docNumber || '—'}</span></div>
                  <div className="flex justify-between"><span className="text-[#848E9C]">Front</span><span className="text-white font-mono">{idFront ? `${dataUrlSizeKb(idFront)} KB ✓` : '—'}</span></div>
                  {docType !== 'passport' && (
                    <div className="flex justify-between"><span className="text-[#848E9C]">Back</span><span className="text-white font-mono">{idBack ? `${dataUrlSizeKb(idBack)} KB ✓` : '—'}</span></div>
                  )}
                  <div className="flex justify-between"><span className="text-[#848E9C]">Selfie</span><span className="text-white font-mono">{selfie ? `${dataUrlSizeKb(selfie)} KB ✓` : '—'}</span></div>
                </div>

                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => setStep(2)}
                    className="border-[#2B3139] bg-[#0B0E11] text-white hover:bg-[#2B3139] rounded-sm flex-1">Back</Button>
                  <Button onClick={submit} disabled={!canSubmit}
                    className="bg-[#FCD535] hover:bg-[#F0B90B] text-[#0B0E11] font-semibold rounded-sm flex-1 disabled:opacity-40"
                    data-testid="kyc-submit-btn">
                    {submitting ? 'Submitting…' : 'Submit for Review'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* Post-submit next steps */}
      {kycStatus === 'pending' && (
        <div className="border border-[#FCD535]/30 bg-[#FCD535]/5 p-4 rounded-sm text-sm text-[#FCD535] mt-6" data-testid="kyc-pending-hint">
          <div className="font-semibold uppercase mb-1">What happens next?</div>
          <div className="text-[#B7BDC6]">
            You'll get an email as soon as an administrator reviews your submission.
            You can keep trading — you'll only be blocked from withdrawals above the non-KYC cap.
          </div>
        </div>
      )}
      {kycStatus === 'verified' && (
        <div className="border border-[#0ECB81]/30 bg-[#0ECB81]/5 p-4 rounded-sm text-sm text-[#0ECB81] mt-6" data-testid="kyc-verified-cta">
          <div className="font-semibold uppercase mb-1">Tier 1 · You're all set</div>
          <div className="text-[#B7BDC6]">
            Head to <a href="/wallet" className="underline text-[#FCD535]">Wallet</a> — the non-KYC cap is gone.
          </div>
        </div>
      )}

      {/* Tier 2 upgrade section — only visible when Tier 1 done, Tier 2 not yet */}
      {kycStatus === 'verified' && tier < 2 && !pendingTier2 && (
        <div className="mt-6 border border-[#0ECB81]/30 bg-[#181A20] p-6 space-y-4" data-testid="kyc-tier2-section">
          <div className="flex items-start gap-3">
            <ShieldCheck className="w-6 h-6 text-[#0ECB81] flex-shrink-0 mt-1" />
            <div className="flex-1">
              <div className="text-white text-lg font-semibold">Upgrade to Tier 2 · Remove all limits</div>
              <div className="text-sm text-[#B7BDC6] mt-1">
                Add a proof-of-address (utility bill, bank statement, tenancy agreement) to lift the Tier 1 cap and enjoy <strong className="text-[#0ECB81]">unlimited withdrawals</strong>.
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <Label className="text-[#B7BDC6] text-xs uppercase tracking-wider">Address line 1</Label>
              <Input value={t2Addr1} onChange={e => setT2Addr1(e.target.value)}
                placeholder="Street address (must match doc)"
                className="bg-[#0B0E11] border-[#2B3139] rounded-sm h-11 text-white mt-1.5"
                data-testid="kyc-t2-addr1" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-[#B7BDC6] text-xs uppercase tracking-wider">Address line 2 (optional)</Label>
              <Input value={t2Addr2} onChange={e => setT2Addr2(e.target.value)}
                className="bg-[#0B0E11] border-[#2B3139] rounded-sm h-11 text-white mt-1.5"
                data-testid="kyc-t2-addr2" />
            </div>
            <div>
              <Label className="text-[#B7BDC6] text-xs uppercase tracking-wider">City</Label>
              <Input value={t2City} onChange={e => setT2City(e.target.value)}
                className="bg-[#0B0E11] border-[#2B3139] rounded-sm h-11 text-white mt-1.5"
                data-testid="kyc-t2-city" />
            </div>
            <div>
              <Label className="text-[#B7BDC6] text-xs uppercase tracking-wider">Postal Code</Label>
              <Input value={t2Postal} onChange={e => setT2Postal(e.target.value)}
                className="bg-[#0B0E11] border-[#2B3139] rounded-sm h-11 text-white mt-1.5 font-mono"
                data-testid="kyc-t2-postal" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-[#B7BDC6] text-xs uppercase tracking-wider">Country</Label>
              <Select value={t2Country} onValueChange={setT2Country}>
                <SelectTrigger className="bg-[#0B0E11] border-[#2B3139] rounded-sm text-white mt-1.5" data-testid="kyc-t2-country"><SelectValue placeholder="Select country" /></SelectTrigger>
                <SelectContent className="bg-[#181A20] border-[#2B3139] text-white max-h-72">
                  {COUNTRIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label className="text-[#B7BDC6] text-xs uppercase tracking-wider">Proof type</Label>
              <Select value={t2ProofType} onValueChange={setT2ProofType}>
                <SelectTrigger className="bg-[#0B0E11] border-[#2B3139] rounded-sm text-white mt-1.5" data-testid="kyc-t2-prooftype"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#181A20] border-[#2B3139] text-white">
                  <SelectItem value="utility_bill">Utility bill (electric/water/gas · &lt; 3 months old)</SelectItem>
                  <SelectItem value="bank_statement">Bank statement (&lt; 3 months old)</SelectItem>
                  <SelectItem value="tenancy_agreement">Tenancy agreement</SelectItem>
                  <SelectItem value="government_letter">Government letter (tax, voter reg, etc.)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <FileDrop label="Proof-of-address document"
            hint="Must clearly show your full name + address + a recent date. PDF pages can be screenshotted."
            value={t2Proof} onChange={setT2Proof} testid="kyc-t2-proof" />
          <Button onClick={submitTier2}
            disabled={t2Submitting || !t2Addr1 || !t2City || !t2Postal || !t2Country || !t2Proof}
            className="bg-[#0ECB81] hover:bg-[#0AB673] text-[#0B0E11] font-semibold rounded-sm w-full h-11 disabled:opacity-40"
            data-testid="kyc-t2-submit-btn">
            {t2Submitting ? 'Submitting Tier 2…' : 'Submit Tier 2 for Review'}
          </Button>
        </div>
      )}
      {pendingTier2 && (
        <div className="mt-6 border border-[#F0B90B]/30 bg-[#F0B90B]/5 p-4 rounded-sm text-sm text-[#F0B90B]" data-testid="kyc-tier2-pending">
          <div className="font-semibold uppercase mb-1">Tier 2 under review</div>
          <div className="text-[#B7BDC6]">You'll receive an email once your proof-of-address is verified. Meanwhile you remain Tier 1.</div>
        </div>
      )}
      {tier >= 2 && (
        <div className="mt-6 border border-[#0ECB81]/40 bg-[#0ECB81]/10 p-4 rounded-sm text-sm text-[#0ECB81]" data-testid="kyc-tier2-done">
          <div className="font-semibold uppercase mb-1">Tier 2 · Fully verified</div>
          <div className="text-[#B7BDC6]">Congratulations — your withdrawal cap is now unlimited.</div>
        </div>
      )}
    </div>
  );
}
